import { Navigate } from 'react-router-dom';
import { useAuthUser } from '../contexts/AuthUserContext';
import { useCustomer } from '../contexts/CustomerContext';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { useNavigation } from '../hooks/useNavigation';

/**
 * Component that redirects users to the appropriate dashboard based on their role
 * and default page preference
 * - helios_admin users go to admin-dashboard (or their preferred default page)
 * - All other users go to home (customer portal) or their preferred default page
 */
const RoleBasedRedirect = () => {
  const user = useAuthUser();
  const { currentCustomer, isLoading, isImpersonating } = useCustomer();
  const { getDefaultPage } = useUserPreferences();
  const adminNavigation = useNavigation('admin');
  const clientNavigation = useNavigation('client');

  // Debug logging
  // console.log('🔍 RoleBasedRedirect - Full User Object:', user);
  // console.log('🔍 RoleBasedRedirect - Is Helios Admin:', user?.isHeliosAdmin);
  // console.log('🔍 RoleBasedRedirect - Roles Array:', user?.roles);
  // console.log('🔍 RoleBasedRedirect - First Role:', user?.role);
  // console.log('🔍 RoleBasedRedirect - Raw Auth0 User:', user?._raw);

  // Wait for user and customer contexts to be fully loaded to avoid race conditions.
  if (isLoading || !user?.email) {
    return null; // Or a loading spinner
  }

  // Get user's preferred default page
  const defaultPage = getDefaultPage();
  // console.log('🔍 RoleBasedRedirect - Default Page Preference:', defaultPage);

  // Helper function to check if a page is valid for the current user
  const isValidPage = (pagePath) => {
    if (user.isHeliosAdmin && !isImpersonating) {
      // For admin users, check if it's a valid admin page
      const adminPages = ['admin-dashboard', 'admin-clients', 'admin-activity', 'admin-security', 'alerts', 'tickets', 'chat', 'settings'];
      return adminPages.includes(pagePath);
    } else {
      // For client users, check if the page is in their navigation
      if (!currentCustomer?.id) return false;
      
      // Check if it's in their available navigation or is a common page
      const availablePages = clientNavigation.navLinks.map(link => {
        const pathParts = link.path.split('/');
        return pathParts[pathParts.length - 1];
      });
      
      const commonPages = ['settings', 'help'];
      return availablePages.includes(pagePath) || commonPages.includes(pagePath);
    }
  };

  // 1. For a Helios Admin who is NOT impersonating
  if (user.isHeliosAdmin && !isImpersonating) {
    // Check if they have a valid default page preference
    if (defaultPage && isValidPage(defaultPage)) {
      return <Navigate to={`/app/${defaultPage}`} replace />;
    }
    // Fallback to admin dashboard
    return <Navigate to="/app/admin-dashboard" replace />;
  }
  
  // 2. For a client user OR an admin who IS impersonating
  if (currentCustomer?.id) {
    // Check if they have a valid default page preference
    if (defaultPage && isValidPage(defaultPage)) {
      return <Navigate to={`/app/${currentCustomer.id}/${defaultPage}`} replace />;
    }
    
    // Fallback to customer's home page
    return <Navigate to={`/app/${currentCustomer.id}/${currentCustomer.id}-home`} replace />;
  }

  // 3. Fallback for a non-admin user whose CustomerContext might still be initializing.
  if (!user.isHeliosAdmin && user.customerId) {
    // Check if they have a valid default page preference
    if (defaultPage && isValidPage(defaultPage)) {
      return <Navigate to={`/app/${user.customerId}/${defaultPage}`} replace />;
    }
    
    // Fallback to customer's home page
    return <Navigate to={`/app/${user.customerId}/${user.customerId}-home`} replace />;
  }

  // Fallback if no condition is met, though this should ideally not be reached.
  return <Navigate to="/" replace />; 
};

export default RoleBasedRedirect;
